import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated — sign in with it now");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-hi flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm rounded-2xl border border-line bg-panel p-8"
        data-testid="reset-screen"
      >
        <Logo />
        <h1 className="font-display text-3xl font-black uppercase tracking-wide mt-6">New password</h1>
        {!token ? (
          <>
            <p className="text-mid text-sm mt-3">This reset link is missing its token.</p>
            <Link to="/app" className="mt-6 inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-mid hover:text-hi transition">
              ← Back to sign in
            </Link>
          </>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
            <input
              data-testid="reset-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="w-full bg-ink border border-line rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-ember"
            />
            <button
              type="submit"
              data-testid="reset-submit-button"
              disabled={busy || !password}
              className="w-full rounded-full bg-ember py-3 font-display text-lg font-bold uppercase tracking-wider text-ink hover:bg-ember/90 disabled:opacity-40 transition"
            >
              {busy ? "Saving…" : "Save new password"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
